import { Alert, Button, Card } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IAdminProcess } from '@common/interfaces';
import type { HolterAdminReadData } from '@common/interfaces/api/processesApi';
import { ManagerAuthorization, ProcessActivityStatus, ProcessStatus, RecorderType } from '@common/enums';
import { holterStepNames } from '@common/utils/serviceWorkflowStepNames/holterStepNames';
import { getHolterAdminProcessData } from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';

export function ServiceProcessActionsHolter(props: Readonly<{ process: IAdminProcess }>) {
    const { process } = props;
    const [data, setData] = useState<HolterAdminReadData | undefined>(undefined);
    const [showUploadJsonAlert, setShowUploadJsonAlert] = useState(false);
    const translate = getSelectedLanguage();
    const canEditAdminProcess = hasOneAuthorization(ManagerAuthorization.adminProcessEdit);
    const completedHolterReadOutActivity = process.activities.find(
        (activity) =>
            activity.serviceActivity.name === holterStepNames.holterReadOut &&
            activity.status === ProcessActivityStatus.complete
    );
    const completedConfigureActivity = process.activities.find(
        (activity) =>
            [holterStepNames.holterConfigure, holterStepNames.directExaminationRequest].includes(
                activity.serviceActivity.name
            ) && activity.status === ProcessActivityStatus.complete
    );

    useEffect(() => {
        if (completedConfigureActivity && process.status === ProcessStatus.active) {
            getHolterProcessData(process.id);
        }
    }, []);

    if (canEditAdminProcess && !completedHolterReadOutActivity && data?.recorderType === RecorderType.bittium) {
        if (data?.downloadProgress !== 100) {
            const url = `/api/processes/${process.id}/ConfiguredByKsyos.dat`;

            return (
                <Card>
                    <Card.Body>
                        <Button
                            color="secondary"
                            onClick={() => window.open(url, '_blank')}
                            size="medium"
                            variant="outline"
                        >
                            {translate.processDetails.generateConfiguredByKsyos}
                        </Button>
                    </Card.Body>
                </Card>
            );
        }

        if (data?.downloadProgress === 100 && data?.uploadProgress !== 100) {
            const url = `/api/processes/${process.id}/upload.json`;

            return (
                <Card>
                    <Card.Body>
                        <Button
                            color="secondary"
                            onClick={() => {
                                setShowUploadJsonAlert(true);
                                window.open(url, '_blank');
                            }}
                            size="medium"
                            variant="outline"
                        >
                            {translate.processDetails.generateUploadJson}
                        </Button>

                        {showUploadJsonAlert && (
                            <Alert appearance="info">{translate.processDetails.checkUploadJson}</Alert>
                        )}
                    </Card.Body>
                </Card>
            );
        }
    }

    return null;

    async function getHolterProcessData(processId: string) {
        const result = await getHolterAdminProcessData(processId);
        setData(result);
    }
}
